import { useBehavior, useButton, useTriggerAction } from '@libs/hooks';
import { TButtonData, TWidgetProps } from '@libs/types';
import React from 'react';
import { TButtonOption } from '../../types/options';
import { ButtonRowView } from './ButtonRowView';

/**
 * Standalone ButtonRow widget driven entirely by server-provided configuration.
 * Owns behavior hooks (useBehavior, useButton) and delegates all rendering to ButtonRowView.
 *
 * Use this component when the widget definition comes from the server and no
 * additional style overrides are required. For composable usages where behavior
 * state is managed externally, use ButtonRowView directly.
 */
export const ButtonRowWidget = ({ widget }: TWidgetProps<TButtonData, TButtonOption>) => {
    const { isDisabled, isHidden } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClick } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const options = widget.options ?? {};
    const data = widget.data ?? {};
    const parentStyleNames = [widget.options?.style ?? ' '].filter(Boolean) as string[];

    return (
        <ButtonRowView
            uid={widget.uid}
            label={data.messages?.label ?? data.label}
            parentStyleNames={parentStyleNames}
            isDisabled={isDisabled}
            isHide={isHidden}
            iconName={options.iconName}
            iconVariant={options.iconVariant}
            size={options.size}
            alignment={options.alignment}
            hierarchy={options.hierarchy}
            margin={options.margin}
            onClick={handleClick}
        />
    );
};
